import React, { useMemo, useState } from "react";

import Icon from "@/components/Icon";
import { useModal, UseModalProps } from "@/components/Modal";
import useTranslation from "@/hooks/useTranslation";

import {
  ModalErrorCancelButton,
  ModalErrorCloseButton,
  ModalErrorCode,
  ModalErrorCodeContainer,
  ModalErrorContainer,
  ModalErrorFooter,
  ModalErrorHeader,
  ModalErrorResponseData,
  ModalErrorTitle,
} from "./styles";

interface ModalErrorProps {
  error: Error | any;
  title?: string;
  onClose?: () => void;
}

export default function ErrorModal({ title = "Error", error, onClose }: ModalErrorProps) {
  const { translation } = useTranslation();

  return (
    <ModalErrorContainer>
      <ModalErrorHeader>
        <ModalErrorTitle>{title || "Erro"}</ModalErrorTitle>
        <ModalErrorCloseButton onClick={() => onClose?.()}>
          <Icon name="close-fill" />
        </ModalErrorCloseButton>
      </ModalErrorHeader>
      <ModalErrorCodeContainer>
        <Icon name="alert-line"></Icon>
        <ModalErrorCode>
          {React.isValidElement(error) ? error : error?.stack || String(error)}
        </ModalErrorCode>
        {!!error?.response?.data && (
          <ModalErrorResponseData>
            {typeof error.response?.data === "object"
              ? JSON.stringify(error.response.data, null, 4)
              : error.response.data}
          </ModalErrorResponseData>
        )}
      </ModalErrorCodeContainer>
      <ModalErrorFooter>
        <ModalErrorCancelButton onClick={() => onClose?.()}>
          {translation.modal.close}
        </ModalErrorCancelButton>
      </ModalErrorFooter>
    </ModalErrorContainer>
  );
}

interface UseErrorModalProps extends UseModalProps {
  title?: string;
  defaultError?: any;
}

export function useErrorModal({
  title,
  defaultError,
  ...props
}: UseErrorModalProps = {}) {
  const [error, setError] = useState<any>(defaultError);

  const Element = useMemo(
    () => <ErrorModal error={error} title={title} />,
    [error, title]
  );

  const modalHelpers = useModal(Element, props);

  return {
    ...modalHelpers,
    error,
    show: (err: any) => {
      setError(err);
      modalHelpers.show();
    },
    toggle: (show: boolean, err?: any) => {
      setError(err);
      modalHelpers.toggle(show);
    },
  };
}
